"use client";

import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import { GlowingButton } from "@/components/ui/GlowingButton";
import Link from "next/link";

export function PricingSection() {
  return (
    <section className="py-24 relative border-t border-white/10 bg-black/40 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-wide mb-4 drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
            商业化定价方案
          </h2>
          <div className="w-24 h-1 bg-neon-cyan mx-auto rounded-full shadow-[0_0_10px_rgba(0,243,255,0.5)] mb-6"></div>
          <p className="text-xl text-white/90 drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] max-w-2xl mx-auto">
            选择最适合您的 旺旺助手2.0 接入方案，开始主宰牌桌。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
          {/* Basic Tier */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ delay: 0.1 }}
            className="glass-panel p-8 rounded-xl border border-white/10 relative overflow-hidden group hover:border-neon-cyan/50 transition-colors flex flex-col h-full"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-neon-cyan opacity-50 group-hover:opacity-100 transition-opacity"></div>
            <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">初级服务</h3>
            <p className="text-gray-200 text-sm mb-6 drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">适合个人专业玩家或小型工作室</p>
            <div className="mb-6">
              <span className="text-4xl font-bold text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">$4,200</span>
              <span className="text-gray-300 drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]"> / 月</span>
              <div className="text-xs text-neon-cyan mt-1 font-mono drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">≈ ¥30,000 RMB</div>
            </div>
            <ul className="space-y-4 mb-8 flex-grow">
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-cyan shrink-0" />
                <span>标准 API 接口调用权限</span>
              </li>
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-cyan shrink-0" />
                <span>支持 NLH (德州扑克) 基础玩法</span>
              </li>
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-cyan shrink-0" />
                <span>基础防封号参数配置</span>
              </li>
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-cyan shrink-0" />
                <span>工作日 9:00-18:00 技术支持</span>
              </li>
            </ul>
            <div className="mt-auto">
              <Link href="/contact" className="block">
                <GlowingButton variant="cyan" className="w-full text-sm py-2">选择初级服务</GlowingButton>
              </Link>
            </div>
          </motion.div>

          {/* Pro Tier */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ delay: 0.2 }}
            className="glass-panel p-8 rounded-xl border border-neon-magenta shadow-[0_0_30px_rgba(255,0,255,0.15)] relative overflow-hidden group flex flex-col h-full"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-neon-magenta"></div>
            <div className="absolute top-4 right-4 bg-neon-magenta text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-[0_0_10px_rgba(255,0,255,0.5)]">
              Most Popular
            </div>
            <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">中级服务</h3>
            <p className="text-gray-200 text-sm mb-6 drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">适合中大型俱乐部与平台运营商</p>
            <div className="mb-6">
              <span className="text-4xl font-bold text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">$14,000</span>
              <span className="text-gray-300 drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]"> / 月</span>
              <div className="text-xs text-neon-magenta mt-1 font-mono drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">≈ ¥100,000 RMB</div>
            </div>
            <ul className="space-y-4 mb-8 flex-grow">
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-magenta shrink-0" />
                <span>高并发 WebSocket 实时接口</span>
              </li>
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-magenta shrink-0" />
                <span>支持全玩法 (NLH, PLO4/5/6, 短牌等)</span>
              </li>
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-magenta shrink-0" />
                <span>高级 AI 剥削模型与动态对手分析</span>
              </li>
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-magenta shrink-0" />
                <span>军工级防封号伪装集群</span>
              </li>
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-magenta shrink-0" />
                <span>7x24 小时专属技术响应</span>
              </li>
            </ul>
            <div className="mt-auto">
              <Link href="/contact" className="block">
                <GlowingButton variant="magenta" className="w-full text-sm py-2">选择中级服务</GlowingButton>
              </Link>
            </div>
          </motion.div>

          {/* Custom Tier - Enhanced */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ delay: 0.3 }}
            className="p-8 rounded-xl border border-neon-blue shadow-[0_0_40px_rgba(0,102,255,0.2)] bg-gradient-to-b from-neon-blue/10 to-black/60 backdrop-blur-md relative overflow-hidden group hover:shadow-[0_0_60px_rgba(0,102,255,0.4)] transition-all duration-300 flex flex-col h-full"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-neon-blue"></div>
            <div className="absolute -right-10 -top-10 w-32 h-32 bg-neon-blue/20 rounded-full blur-[30px] group-hover:bg-neon-blue/40 transition-colors"></div>
            
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-5 h-5 text-neon-blue fill-neon-blue shrink-0" />
              <h3 className="text-2xl font-bold text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">高级定制生态</h3>
            </div>
            <p className="text-gray-200 text-sm mb-6 drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">适合顶级平台方或构建底层生态，旺旺助手2.0 的终极定制服务</p>
            <div className="mb-6">
              <span className="text-4xl font-bold text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">面议</span>
              <div className="text-xs text-neon-blue mt-1 font-mono drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">Custom Pricing & Solutions</div>
            </div>
            <ul className="space-y-4 mb-8 flex-grow">
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-blue shrink-0" />
                <span className="font-medium text-neon-blue">平台级私有化部署方案</span>
              </li>
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-blue shrink-0" />
                <span>源码级整合与定制开发</span>
              </li>
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-blue shrink-0" />
                <span>特定平台反检测系统深度定制</span>
              </li>
              <li className="flex items-start gap-3 text-white/90 text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                <Check className="w-5 h-5 text-neon-blue shrink-0" />
                <span>专属 AI 模型训练与微调</span>
              </li>
            </ul>
            <div className="mt-auto">
              <Link href="/contact" className="block">
                <GlowingButton variant="blue" className="w-full text-sm py-2 bg-neon-blue/20">联系商务洽谈</GlowingButton>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
